import React, { useState, useRef } from 'react';
import { ButtonV2, Flex, FormControl, InputV2, SelectV1, TextArea, TextV2, IconV2, useToast, Spinner } from '@withjoy/joykit';
import { AdminSidebarComponentProps } from '../JsonComponent.types';
import { Close, Trash, Photo, WarningTriangle } from '@withjoy/joykit/icons';
import { useGoToDeleteModal, useGoToReorderView } from '../hooks/useJsonComponentNavigation';
import { categories } from './ThingsToDo.constants';
import { useFormik } from 'formik';
import type { FormikProps } from 'formik';
import { useSaveJsonData, useCreateNewJsonComponent } from '../hooks/useJsonBlobStorageUtils';
import { ThingsToDoConfig } from './ThingsToDo.config';
import { useImageFilePicker } from '@apps/admin/common/hooks/useImageFilePicker';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import * as Yup from 'yup';
import { useTranslation } from '@shared/core';
import { useThingToDoCategoryUtils } from '@apps/guest/packages/layout-engine/widgets/Custom/components/ThingToDo/hooks';
import { PhotoUploadPlaceholder, ImagePreview } from './ThingToDo.styles';
import { useWebsiteEditorThingsToDoTelemetry } from '../DefaultThingsToDo/DefaultThingsToDo.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { PointOfInterest, ThingsToDo } from './ThingsToDo.types';
import { useDeletePageComponentV2Mutation } from '@graphql/generated';
import { useUrlMetadataUnfurl, useFieldDirtyState } from './hooks';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

/**
 * Normalizes a URL by ensuring it has https:// protocol.
 * - Adds https:// if no protocol is present
 * - Converts http:// to https://
 * @param url The raw URL input
 * @returns The normalized URL, or empty string if input is empty
 */
function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';

  // Check if URL already has a protocol
  if (/^https:\/\//i.test(trimmed)) {
    return trimmed;
  }

  // Convert http:// to https://
  if (/^http:\/\//i.test(trimmed)) {
    return trimmed.replace(/^http:\/\//i, 'https://');
  }

  // Add https:// if no protocol present
  return `https://${trimmed}`;
}

/** Field names that can be auto-filled from URL metadata */
type AutoFillableField = 'title' | 'personalNote' | 'image';
// Initial data for new components
export const INITIAL_DATA: ThingsToDo = {
  lang: 'en',
  location: { city: 'N/A' },
  poi: {
    id: '',
    title: '',
    url: '',
    category: 'attraction',
    description: '',
    tours: [],
  },
};

interface ThingsToDoFormValues {
  title: string;
  url: string;
  category: string;
  personalNote: string;
  customCategory: string;
}

export const ThingsToDoEdit = (props: AdminSidebarComponentProps) => {
  const { isEditMode } = props;
  const { tq } = useTranslation('thingsToDoCustomComponent');

  const telemetry = useWebsiteEditorThingsToDoTelemetry();
  const eventInfo = useEventInfo();
  const { goToReorderView } = useGoToReorderView();
  const { goToDeleteModal } = useGoToDeleteModal();
  const { saveJsonData } = useSaveJsonData();
  const { createNewJsonComponent } = useCreateNewJsonComponent(props.eventHandle);
  const [deletePageComponentV2] = useDeletePageComponentV2Mutation();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { setRefetchQueries } = useCustomComponentStateContext();
  const thingsToDoContent: ThingsToDo | null = props.componentData.containerContent.content;
  const { openFilepicker, updatedImage, imageName, uploadImage, setImageFromUrl } = useImageFilePicker({
    eventId: props.componentData.containerContent.eventId,
    initialImageUrl: props.componentData.containerContent.content.poi?.image?.url || undefined,
  });
  const { getCategoryIcon } = useThingToDoCategoryUtils();
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // In edit mode, mark fields with existing saved content as dirty to prevent unfurl from overwriting them.
  // This ensures that when a user clicks on the URL field (e.g., to copy it) and blurs,
  // their carefully curated content isn't replaced with metadata from the URL.
  const initialDirtyFields: AutoFillableField[] = [];
  if (isEditMode && thingsToDoContent?.poi) {
    if (thingsToDoContent.poi.title) initialDirtyFields.push('title');
    if (thingsToDoContent.poi.description) initialDirtyFields.push('personalNote');
    if (thingsToDoContent.poi.image?.url) initialDirtyFields.push('image');
  }

  // Track which fields have been manually edited by the user (to avoid overwriting with unfurl data)
  const { isFieldDirty, markFieldDirty } = useFieldDirtyState<AutoFillableField>(initialDirtyFields);

  // Ref to hold formik instance for use in callbacks that are created before formik
  const formikRef = useRef<FormikProps<ThingsToDoFormValues> | null>(null);

  // URL to trigger metadata fetch (set on blur)
  const [urlToFetch, setUrlToFetch] = useState<string | null>(null);

  // URL metadata unfurl hook for auto-filling form fields
  const unfurlResult = useUrlMetadataUnfurl({
    url: urlToFetch,
    onSuccess: async metadata => {
      const formikInstance = formikRef.current;
      if (!formikInstance) return;

      // Only auto-fill fields that haven't been manually edited
      if (metadata.title && !isFieldDirty('title')) {
        formikInstance.setFieldValue('title', metadata.title);
      }
      if (metadata.description && !isFieldDirty('personalNote')) {
        formikInstance.setFieldValue('personalNote', metadata.description);
      }
      if (metadata.imageUrl && !isFieldDirty('image')) {
        // Unlike user input (which we can normalize with normalizeUrl), this is external metadata
        // we don't control. We can only accept or reject it - there's no safe way to "fix" a
        // data: or javascript: URI, so we only allow http(s) URLs.
        // Upgrade http:// to https:// since most modern sites support HTTPS and this avoids
        // mixed content issues. If the HTTPS version fails, the image simply won't load.
        let imageUrl = metadata.imageUrl;
        if (imageUrl.startsWith('http://')) {
          imageUrl = imageUrl.replace('http://', 'https://');
        }
        if (imageUrl.startsWith('https://')) {
          await setImageFromUrl(imageUrl);
        }
      }
    },
    onError: errorMessage => {
      // Only show error toast if it's a meaningful error (not just "URL not found" type errors)
      if (errorMessage && !errorMessage.toLowerCase().includes('not found')) {
        toast(tq`Unable to fetch page info`, { icon: <WarningTriangle />, duration: 3000 });
      }
    },
  });
  const unfurlLoading = unfurlResult.status === 'loading';

  const validationSchema = Yup.object({
    title: Yup.string().required(tq`Name is required`),
    url: Yup.string().test('url-or-empty', tq`Please enter a valid URL`, value => !value || Yup.string().url().isValidSync(value)),
    category: Yup.string().required(tq`Category is required`),
    personalNote: Yup.string().required(),
    customCategory: Yup.string().when('category', {
      is: (val: string) => val === 'custom',
      then: Yup.string().required(tq`Category is required`),
      otherwise: Yup.string().notRequired(),
    }),
  });

  // Determine if saved category is custom (not in predefined list)
  const savedCategory = thingsToDoContent?.poi.category || 'attraction';
  const isCustomCategory = !categories.find(c => c.value === savedCategory);
  const initialCategory = isCustomCategory ? 'custom' : savedCategory;
  const initialCustomCategory = isCustomCategory ? savedCategory : '';

  const initialValues: ThingsToDoFormValues = {
    title: thingsToDoContent?.poi.title || '',
    url: thingsToDoContent?.poi.url || '',
    category: initialCategory,
    personalNote: thingsToDoContent?.poi.description || '',
    customCategory: initialCustomCategory,
  };
  const isOwnContainer = props.componentData.containerContent.eventId === eventInfo.eventInfo?.eventId;

  const isGetYourGuideUrl = initialValues.url?.toLowerCase().includes('getyourguide.com') || false;

  const formik = useFormik<ThingsToDoFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      setLoading(true);
      let uploadedImage = undefined;
      if (updatedImage && imageName) {
        uploadedImage = await uploadImage();
      }
      const poiData: PointOfInterest & { category: string; description: string } = {
        ...thingsToDoContent?.poi,
        id: thingsToDoContent?.poi.id || values.title + Date.now(),
        title: values.title,
        url: values.url,
        category: values.category === 'custom' ? values.customCategory : values.category,
        description: values.personalNote,
        image: uploadedImage ? { url: uploadedImage.url, id: uploadedImage.id } : thingsToDoContent?.poi.image,
      };

      const componentData = {
        lang: 'en',
        location: { city: 'N/A' },
        ...thingsToDoContent,
        poi: poiData,
      };

      if (isEditMode && isOwnContainer) {
        try {
          await saveJsonData({
            id: props.componentData.containerContent.id,
            content: componentData,
            type: ThingsToDoConfig.componentType,
            schemaUrl: ThingsToDoConfig.schemaUrl,
          });
          telemetry.updateThingsToDoClicked(props.pageSlug, {
            title: componentData.poi.title,
            url: componentData.poi.url,
            category: componentData.poi.category,
            personalNote: componentData.poi.description,
            image: componentData.poi.image?.url || undefined,
          });
          // Request a refetch of the components list to update the left panel
          setRefetchQueries(['PageContainerBySlug']);
          updateWebsitePreview();
          setLoading(false);
          goToReorderView();
        } catch {
          setLoading(false);
          toast(tq`Failed to update. Please try again.`, { icon: <WarningTriangle />, duration: 5000 });
        }
      } else {
        try {
          const result = await createNewJsonComponent({
            schemaURL: ThingsToDoConfig.schemaUrl,
            type: ThingsToDoConfig.componentType,
            initialData: componentData,
            containerId: props.containerId,
            sortOrder: props.componentData.sortOrder,
          });
          if (props.componentData.id) {
            await deletePageComponentV2({
              variables: {
                payload: {
                  id: props.componentData.id,
                },
              },
            });
          }
          if (result?.containerData?.data) {
            telemetry.saveThingsToDoClicked(props.pageSlug, {
              title: componentData.poi.title,
              url: componentData.poi.url,
              category: componentData.poi.category,
              personalNote: componentData.poi.description,
              image: componentData.poi.image?.url || undefined,
            });
            // Request a refetch of the components list to update the left panel
            setRefetchQueries(['PageContainerBySlug']);
            updateWebsitePreview();
            setLoading(false);
            goToReorderView();
          }
        } catch {
          setLoading(false);
          toast(tq`Failed to create. Please try again.`, { icon: <WarningTriangle />, duration: 5000 });
        }
      }
    },
  });

  // Update formik ref so callbacks can access it
  formikRef.current = formik;

  // Handler for URL input blur - normalizes the URL and triggers metadata fetch
  const handleUrlBlur = useEventCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const rawUrl = e.target.value;
    if (rawUrl.trim()) {
      const normalizedUrl = normalizeUrl(rawUrl);

      // Update the field with normalized URL if different
      if (normalizedUrl !== rawUrl) {
        formik.setFieldValue('url', normalizedUrl, true);
        formik.setFieldTouched('url', true, false);
      } else {
        formik.handleBlur(e);
      }

      // Trigger metadata fetch by setting the URL
      setUrlToFetch(normalizedUrl);
    } else {
      formik.handleBlur(e);
    }
  });

  // Handlers that track when user manually edits auto-fillable fields
  const handleTitleChange = useEventCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    markFieldDirty('title');
    formik.getFieldProps('title').onChange(e);
  });

  const handlePersonalNoteChange = useEventCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    markFieldDirty('personalNote');
    formik.getFieldProps('personalNote').onChange(e);
  });

  const handleImageClick = useEventCallback(() => {
    markFieldDirty('image');
    openFilepicker();
  });

  return (
    <>
      <Flex
        flexDirection="column"
        paddingX={8}
        paddingY={6}
        position="relative"
        overflow="auto"
        height="100%"
      >
        <Flex
          cursor="pointer"
          onClick={goToReorderView}
          position="absolute"
          top={6}
          right={6}
          border="1px solid"
          borderColor="mono3"
          padding={1}
          borderRadius={'50%'}
        >
          <Close size={24} />
        </Flex>
        <Flex
          justifyContent="center"
          alignItems="center"
          marginBottom={6}
        >
          <TextV2 typographyVariant="hed6">{isEditMode ? tq`Edit Thing to Do` : tq`Add Thing to Do`}</TextV2>
        </Flex>
        <form onSubmit={formik.handleSubmit}>
          <Flex
            flexDirection="column"
            gap={6}
          >
            <FormControl
              label={tq`Category`}
              isInvalid={formik.touched.category && !!formik.errors.category}
              error={formik.errors.category}
            >
              <SelectV1
                autoComplete="off"
                searchable={false}
                iconLeft={<IconV2>{getCategoryIcon(formik.values.category || 'attraction')}</IconV2>}
                options={categories}
                value={categories.find(c => c.value === formik.values.category)}
                onChange={option => formik.setFieldValue('category', option?.value || 'attraction')}
              />
            </FormControl>
            {formik.values.category === 'custom' && (
              <FormControl
                label={tq`Custom Category`}
                isInvalid={formik.touched.customCategory && !!formik.errors.customCategory}
                error={formik.errors.customCategory}
              >
                <InputV2
                  placeholder={tq`Custom Category`}
                  value={formik.values.customCategory}
                  onChange={e => formik.setFieldValue('customCategory', e.target.value)}
                />
              </FormControl>
            )}
            {!isGetYourGuideUrl && (
              <FormControl
                label={tq`Website`}
                isInvalid={formik.touched.url && !!formik.errors.url}
                error={formik.errors.url}
              >
                <InputV2
                  placeholder={tq`thingtodo.com`}
                  {...formik.getFieldProps('url')}
                  onBlur={handleUrlBlur}
                  endElement={unfurlLoading ? <Spinner /> : undefined}
                />
              </FormControl>
            )}
            <FormControl
              label={tq`Name`}
              isInvalid={formik.touched.title && !!formik.errors.title}
              error={formik.errors.title}
            >
              <InputV2
                placeholder={tq`Louvre Museum`}
                {...formik.getFieldProps('title')}
                onChange={handleTitleChange}
              />
            </FormControl>
            <PhotoUploadPlaceholder onClick={handleImageClick}>
              {updatedImage ? (
                <ImagePreview src={updatedImage} />
              ) : (
                <Photo
                  size={48}
                  color={'mono8'}
                />
              )}
            </PhotoUploadPlaceholder>
            <FormControl
              label={tq`Personal Note`}
              isInvalid={formik.touched.personalNote && !!formik.errors.personalNote}
              error={formik.errors.personalNote}
            >
              <TextArea
                placeholder={tq`Add some optional details about why you think your guests would enjoy it, things to know, etc.`}
                minRows={4}
                {...formik.getFieldProps('personalNote')}
                onChange={handlePersonalNoteChange}
              />
            </FormControl>
            {isEditMode && (
              <Flex
                justifyContent="center"
                alignItems="center"
              >
                <Flex
                  gap={3}
                  cursor="pointer"
                  alignItems="center"
                  onClick={() => goToDeleteModal(props.componentData)}
                >
                  <Trash
                    size={24}
                    color="negative6"
                  />
                  <TextV2
                    typographyVariant="button2"
                    color="negative6"
                  >
                    {tq`Delete Thing to Do`}
                  </TextV2>
                </Flex>
              </Flex>
            )}
            <Flex
              justifyContent="space-around"
              alignItems="center"
              marginTop={9}
            >
              <ButtonV2
                flex={1}
                shape="rounded"
                variant="ghost"
                intent="neutral"
                onClick={goToReorderView}
              >
                {tq`Cancel`}
              </ButtonV2>
              <ButtonV2
                type="submit"
                flex={1}
                shape="rounded"
                intent="neutral"
                loading={loading}
              >
                {tq`Save`}
              </ButtonV2>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </>
  );
};
